import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import Loading from '../../../feedback/loading.jsx';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../ui/alert-dialog.jsx';
import { Button } from '../../../ui/button.jsx';
import { Checkbox } from '../../../ui/checkbox.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';
import { Textarea } from '../../../ui/textarea.jsx';

const voidInvoiceSchema = yup.object().shape({
  confirmed: yup
    .boolean()
    .oneOf([true], 'Deve confirmar que compreende as consequências'),
  reason: yup.string().required('Deve especificar o motivo'),
});

export const DialogVoidInvoiceConfirmation = ({
  button,
  action,
  recordName,
  redirect,
  message,
  alerts = true,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formVoidInvoice = useForm({
    resolver: yupResolver(voidInvoiceSchema),
    mode: 'onChange',
    defaultValues: {
      confirmed: false,
      reason: '',
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await action(data.reason);

      if (redirect) {
        navigate(redirect);
      }

      alerts && toast.success('Fatura anulada com sucesso');
      setOpen(false);
      formVoidInvoice.reset();
    } catch (err) {
      alerts && toast.error('Erro ao anular Fatura');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    if (!newOpen) {
      formVoidInvoice.reset();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">
          <XIcon className="size-4" />
          Anular
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent>
        {isLoading ? (
          <div className="flex w-full justify-center">
            <Loading message="A anular fatura" />
          </div>
        ) : (
          <Form {...formVoidInvoice}>
            <form onSubmit={formVoidInvoice.handleSubmit(onSubmit)}>
              <AlertDialogHeader>
                <AlertDialogTitle>Tem a certeza?</AlertDialogTitle>
                <AlertDialogDescription>
                  <span>
                    {message || (
                      <span>
                        A fatura - <b>{recordName}</b> - será anulada e não será
                        contabilizada para o sorteio da campanha.
                      </span>
                    )}
                  </span>
                </AlertDialogDescription>
                <AlertDialogDescription className="font-bold text-red-500">
                  Esta ação é irreversível.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="flex flex-col gap-y-4 py-4">
                <FormField
                  control={formVoidInvoice.control}
                  name="confirmed"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <div className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            className="mr-1 size-5"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="ml-4 space-y-0 font-normal">
                          Eu concordo que esta ação tem consequências
                          irreversíveis e que mesmo assim quero realizá-la.
                        </FormLabel>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formVoidInvoice.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel required={true}>Descrição do Motivo</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Escreva o motivo da anulação"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel>
                  <XIcon />
                  Cancelar
                </AlertDialogCancel>
                <Button
                  type="submit"
                  disabled={!formVoidInvoice.formState.isValid || isLoading}
                  variant="destructive"
                >
                  <XIcon />
                  Anular
                </Button>
              </AlertDialogFooter>
            </form>
          </Form>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
